import React, { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import Joyride, { TooltipRenderProps } from "react-joyride";

import { Box } from "ds/Box";
import { Button } from "ds/Button";
import { BorderRadius200 } from "ds/Core";
import { Text } from "ds/Typography";
import { Icon } from "ds/icons";
import { useGlobalContext } from "hooks/useGlobalContext";
import { isDateNewerThan } from "utils/date";

import { StyledCloseButton, StyledTooltipContainer } from "./styles";

export type Props = {
  title?: string;
  description: ReactNode;
  active: boolean;
  onDismiss?: () => void;
  videoLink?: string;
  learnMoreUrl?: string;
  actions?: ReactNode;
  containerId?: string;
  launchDate?: string;
};

export const FeatureCallout = ({
  title,
  videoLink,
  onDismiss,
  description,
  active,
  learnMoreUrl,
  actions,
  containerId = "feature-callout",
  launchDate,
}: Props) => {
  // check if the user is created after the launch date
  // if that's the case, no need to tell them what's new in the product
  const { currentOrganisation } = useGlobalContext();
  const accountCreatedDate = currentOrganisation?.myAccount?.insertedAt;
  const isRecentAccount = isDateNewerThan(accountCreatedDate, launchDate);

  if (isRecentAccount) return null;

  return (
    <Joyride
      run={active}
      callback={(state) => {
        if (state.status === "finished") {
          onDismiss?.();
        }
      }}
      styles={{
        options: {
          spotlightShadow: "none",
        },
      }}
      steps={[
        {
          target: `#${containerId}`,
          title,
          content: description,
          disableBeacon: true,
          disableOverlay: true,
          data: { videoLink, learnMoreUrl, actions },
        },
      ]}
      tooltipComponent={CustomTooltip}
      floaterProps={{ disableAnimation: true, hideArrow: true }}
    />
  );
};

const CustomTooltip = ({ closeProps, step, tooltipProps }: TooltipRenderProps) => {
  const { t } = useTranslation();
  return (
    <StyledTooltipContainer {...tooltipProps}>
      <StyledCloseButton
        variation="secondary"
        size="micro"
        icon={<Icon size="small" name="XCloseIcon" />}
        {...closeProps}
      />
      {step.data.videoLink ? (
        <iframe
          referrerPolicy="strict-origin-when-cross-origin"
          src={step.data.videoLink}
          allowFullScreen
          style={{ border: 0, borderRadius: `${BorderRadius200} ${BorderRadius200} 0 0`, aspectRatio: "16/9" }}
        />
      ) : (
        <Box mt="200" />
      )}
      <Box p="200" pb="100">
        {step.title && (
          <Text lineHeight="loose" preset="paragraph2-200">
            {step.title}
          </Text>
        )}
        <Text preset="small100" color="textColorSubtle" mt="50" mb="100">
          {step.content}
        </Text>
      </Box>
      {step.data.actions}
      {step.data.learnMoreUrl && (
        <Box p="200" pt="0">
          <Button size="micro" url={step.data.learnMoreUrl} text={t("btn.learn-more")} />
        </Box>
      )}
    </StyledTooltipContainer>
  );
};

export const FeatureCalloutTarget = () => (
  <div style={{ position: "absolute", bottom: -10, left: 10 }} id="feature-callout" />
);
